// src/components/onboarding/ConsentsCard.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import {
  getSettingsConsents,
  setConsent,
} from "../../shared/_services/onboarding.services";
import { getPlaidLinks } from "../../shared/_services/plaid.services";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Panel,
} from "../ui";
import {
  DISCONNECT_FAILED,
  TURN_OFF_BODY,
  TURN_OFF_BODY_ANY,
  TURN_OFF_CANCEL,
  TURN_OFF_CONFIRM,
  TURN_OFF_TITLE,
} from "../plaid/plaidCopy";
import {
  LOAD_FAILED,
  RECEIPT_NOT_YET,
  SAVE_FAILED,
  SETTINGS_CONSENTS,
  SETTINGS_WITHDRAW,
  TRY_AGAIN,
} from "./onboardingCopy";
import { fullDate } from "./onboardingFormat";
import { announceLinksChanged, onLinksChanged } from "../plaid/plaidNav";

// One consent row with Settings semantics (owner decision 10): a switch for a
// kind whose current wording is not `required`, a locked row with the
// withdraw line for one that is. `onToggle` is called with the row; the parent
// writes it.
export function ConsentSettingRow({ row, busy, onToggle }) {
  const labelId = `onb-consent-${row.kind}`;
  return (
    <li
      data-slot="onb-setting"
      className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-divider tw-py-3 last:tw-border-b-0"
    >
      <div data-slot="onb-setting-text">
        <div id={labelId} className="tw-text-sm tw-font-semibold tw-text-text">
          {row.shortLabel}
        </div>
        <div data-slot="onb-muted" className="tw-text-sm tw-text-text-muted">
          {row.at ? fullDate(row.at) : RECEIPT_NOT_YET}
        </div>
        {row.required && (
          <div data-slot="onb-muted" className="tw-text-sm tw-text-text-muted">
            {SETTINGS_WITHDRAW}
          </div>
        )}
      </div>
      <Checkbox
        role="switch"
        aria-labelledby={labelId}
        checked={row.state === true}
        disabled={row.required || busy}
        onChange={() => onToggle(row)}
      />
    </li>
  );
}

// Writes one Settings change: the switch moves at once, the server's row
// replaces it on success, and a failure puts it back and shows the server's
// message. Shared by the Settings card and the consents page's read-only view.
//
// SP6 (owner item 1, amended; spec § Consent withdrawal): switching Plaid off
// unlinks every bank, so with banks linked it asks first — "Turn off Plaid?"
// naming them (`plaidDialog`, which the caller renders). Cancel leaves it on.
// "Turn off and unlink" never moves the switch ahead of the server: it shows
// the server's row; a 503 unlink_failed (the withdrawal is already written)
// says the SP3 line and re-reads GET /consents. With no banks it is a plain
// switch, as before. Either way the Linked accounts panel is told to re-read.
export function useConsentSwitches(setRows) {
  const { enqueueSnackbar } = useSnackbar();
  const [busyKind, setBusyKind] = useState(null);
  const [plaidOff, setPlaidOff] = useState(null);
  const returnFocus = useRef(null);
  const inFlight = useRef(false);
  const alive = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const put = (row, value) =>
    setRows((current) => current.map((r) => (r.kind === row.kind ? value : r)));

  const write = async (row) => {
    const next = row.state !== true;
    setBusyKind(row.kind);
    put(row, { ...row, state: next });
    try {
      const res = await setConsent(row.kind, next);
      put(row, res.data.consent);
      // Linked accounts reads the plaid consent too (R324).
      if (row.kind === "plaid") announceLinksChanged();
    } catch (err) {
      put(row, row);
      enqueueSnackbar(err?.response?.data?.message || SAVE_FAILED, {
        variant: "error",
      });
    } finally {
      setBusyKind(null);
    }
  };
  const turnPlaidOff = async (row) => {
    if (inFlight.current) return;
    setPlaidOff(null);
    inFlight.current = true;
    setBusyKind(row.kind);
    try {
      const res = await setConsent(row.kind, false);
      if (alive.current) put(row, res.data.consent);
    } catch (err) {
      const data = err?.response?.data;
      enqueueSnackbar(
        data?.code === "unlink_failed" ? DISCONNECT_FAILED : SAVE_FAILED,
        { variant: "error" },
      );
      try {
        const fresh = await getSettingsConsents();
        const server = fresh.data.consents.find((c) => c.kind === row.kind);
        if (alive.current) put(row, server ?? row);
      } catch (_) {
        if (alive.current) put(row, row);
      }
    } finally {
      inFlight.current = false;
      announceLinksChanged();
      if (alive.current) {
        setBusyKind(null);
        // The switch was disabled when the dialog closed, so focus was lost.
        setTimeout(() => returnFocus.current?.focus?.(), 0);
      }
    }
  };
  const toggle = async (row) => {
    if (row.kind !== "plaid" || row.state !== true) return write(row);
    if (inFlight.current) return;
    inFlight.current = true;
    returnFocus.current = document.activeElement;
    setBusyKind(row.kind);
    let names = null;
    try {
      const res = await getPlaidLinks();
      names = [...new Set(res.data.links.map((l) => l.institution.name))];
    } catch (_) {
      // Unknown: the warning still says every bank will be unlinked.
    }
    inFlight.current = false;
    if (!alive.current) return;
    setBusyKind(null);
    if (names && names.length === 0) return write(row);
    setPlaidOff({ row, names });
  };

  const plaidDialog = (
    <Dialog
      open={Boolean(plaidOff)}
      onOpenChange={(open) => {
        if (!open) setPlaidOff(null);
      }}
    >
      {plaidOff && (
        <DialogContent
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            returnFocus.current?.focus?.();
          }}
        >
          <DialogTitle>{TURN_OFF_TITLE}</DialogTitle>
          <DialogDescription>
            {plaidOff.names ? TURN_OFF_BODY(plaidOff.names) : TURN_OFF_BODY_ANY}
          </DialogDescription>
          <DialogActions>
            <DialogClose asChild>
              <Button variant="quiet">{TURN_OFF_CANCEL}</Button>
            </DialogClose>
            <Button variant="danger" onClick={() => turnPlaidOff(plaidOff.row)}>
              {TURN_OFF_CONFIRM}
            </Button>
          </DialogActions>
        </DialogContent>
      )}
    </Dialog>
  );
  return { busyKind, toggle, plaidDialog };
}

// Settings › Consents on /profile: every published consent kind, in order.
export default function ConsentsCard() {
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(false);
  const { busyKind, toggle, plaidDialog } = useConsentSwitches(setRows);

  const load = useCallback(async () => {
    setFailed(false);
    try {
      const res = await getSettingsConsents();
      setRows(res.data.consents || []);
    } catch (_) {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);
  // SP6: a consent given while linking from Linked accounts shows here too.
  useEffect(() => onLinksChanged(load), [load]);

  return (
    <Panel as="div" className="tw-mb-4">
      <h4 className="tw-m-0 tw-mb-4 tw-text-lg tw-font-bold tw-text-text">
        {SETTINGS_CONSENTS}
      </h4>
      {failed ? (
        <div role="alert" className="tw-text-sm tw-text-text">
          <span>{LOAD_FAILED}</span>{" "}
          <Button variant="text" size="sm" onClick={load}>
            {TRY_AGAIN}
          </Button>
        </div>
      ) : (
        rows && (
          <ul data-slot="onb-settings" className="tw-m-0 tw-list-none tw-p-0">
            {rows.map((row) => (
              <ConsentSettingRow
                key={row.kind}
                row={row}
                busy={busyKind === row.kind}
                onToggle={toggle}
              />
            ))}
          </ul>
        )
      )}
      {plaidDialog}
    </Panel>
  );
}
